import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import { brandColors } from '@/design';
import { ChevronDown, ChevronRight, Inbox, MoreHorizontal, Pencil } from 'lucide-react';
import type {
  ActionItemStatus,
  DayFocus,
  TaskPlan,
  TodoProject,
  TodoSwimLane,
} from '../../types';
import {
  TODO_LANE_LABELS,
  TODO_PROJECT_COLOR_TOKENS,
  isFocusFullyCompleted,
  isTaskListFullyCompleted,
  sortProjectsForPivot,
  workspaceKanbanColumnForTask,
} from '../../utils/todoSwimLanes';

const WS_COLUMNS: Array<{ id: ActionItemStatus; label: string }> = [
  { id: 'todo', label: 'To do' },
  { id: 'in_progress', label: 'Doing' },
  { id: 'done', label: 'Done' },
];

export type ProjectsPivotRow =
  | { kind: 'task'; id: string; task: TaskPlan }
  | { kind: 'day_focus'; id: string; focus: DayFocus; task: TaskPlan };

/**
 * Synthetic key for the Inbox band when used as the active selection in the
 * project switcher. Real project ids never start with `__`.
 */
export const PROJECT_SWITCHER_INBOX_KEY = '__inbox__';

/** Drop ID for the Kanban-style status columns inside a Project / Inbox band.
 *  Format: `ws-proj-col:${projectKey | '__inbox__'}:${status}`. Parsed in
 *  TodosPage's `handleWorkspaceDragEnd` to (re)assign project + flip the
 *  list's column status in one drop. */
export const PROJECT_COLUMN_DROP_PREFIX = 'ws-proj-col:';

interface ProjectsPivotProps {
  projects: TodoProject[];
  rows: ProjectsPivotRow[];
  /**
   * Renders a single card in a column cell. Caller controls drag handle,
   * project chip, etc. The optional `visibleItemIds` set is provided when
   * the column projection is rendering only a subset of the card's items
   * (a mixed-status TaskPlan appears in every column it has items in,
   * each instance showing just that column's items). `undefined` =
   * render all items, used for empty cards.
   */
  renderCard: (
    row: ProjectsPivotRow,
    opts?: { visibleItemIds?: ReadonlySet<string> }
  ) => ReactNode;
  /**
   * Project header right-side menu: cascade triggers, archive, etc. Receives an
   * `enterRenameMode` callback so the menu's "Rename project…" item can flip
   * the band into inline-edit mode.
   */
  renderProjectMenu: (
    project: TodoProject,
    summary: { active: number; completed: number },
    actions: { enterRenameMode: () => void },
  ) => ReactNode;
  /** Commit a renamed project name (also used by the inline title editor). */
  onRenameProject?: (projectId: string, newName: string) => Promise<void> | void;
  /** Allow user to create a project from inside the empty-state placeholder. */
  onCreateProject: () => void;
  /**
   * When set, narrows the pivot to a single band:
   *  - `null` (default) → show every project + Inbox stacked (legacy behaviour)
   *  - `PROJECT_SWITCHER_INBOX_KEY` → only the Inbox band
   *  - any other value → only the matching project band
   */
  selectedProjectKey?: string | null;
}

/**
 * Project-grouped Kanban surface. Each project (and Inbox) renders its own
 * To-do / Doing / Done columns. Empty pivots show a single empty-state band.
 */
export function ProjectsPivot({
  projects,
  rows,
  renderCard,
  renderProjectMenu,
  onRenameProject,
  onCreateProject,
  selectedProjectKey = null,
}: ProjectsPivotProps) {
  const sortedProjects = useMemo(
    () => sortProjectsForPivot(projects, { includeArchived: false }),
    [projects]
  );

  const archivedProjects = useMemo(
    () => projects.filter((p) => p.archived).sort((a, b) => a.name.localeCompare(b.name)),
    [projects]
  );

  const rowsByProject = useMemo(() => {
    const map = new Map<string, ProjectsPivotRow[]>();
    const inbox: ProjectsPivotRow[] = [];
    for (const row of rows) {
      const projectId = row.task.projectId;
      if (!projectId) {
        inbox.push(row);
        continue;
      }
      const list = map.get(projectId);
      if (list) list.push(row);
      else map.set(projectId, [row]);
    }
    return { map, inbox };
  }, [rows]);

  const inboxRows = rowsByProject.inbox;
  const archivedHasRows = useMemo(
    () => archivedProjects.some((p) => (rowsByProject.map.get(p.id)?.length ?? 0) > 0),
    [archivedProjects, rowsByProject.map]
  );

  if (sortedProjects.length === 0 && inboxRows.length === 0) {
    return (
      <div
        className="rounded-2xl border border-dashed border-[var(--color-border)] p-8 text-center"
        style={{ background: 'var(--color-surface)' }}
      >
        <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
          Group your lists by initiative
        </h3>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1.5 max-w-md mx-auto">
          Create a project to gather related task lists in one place. Cards stay shared with the Schedule view.
        </p>
        <button
          type="button"
          onClick={onCreateProject}
          className="flank-btn flank-btn-primary text-sm px-4 py-2 mt-4"
        >
          Create your first project
        </button>
      </div>
    );
  }

  // Single-project / single-band view selected via the project switcher.
  if (selectedProjectKey === PROJECT_SWITCHER_INBOX_KEY) {
    return (
      <div className="flex flex-col gap-10">
        <InboxBand rows={inboxRows} renderCard={renderCard} />
      </div>
    );
  }
  if (selectedProjectKey) {
    const project = projects.find((p) => p.id === selectedProjectKey) ?? null;
    if (!project) {
      return (
        <div
          className="rounded-2xl border border-dashed border-[var(--color-border)] p-6 text-sm text-[var(--color-text-muted)]"
          style={{ background: 'var(--color-surface)' }}
        >
          That project no longer exists. Pick another from the switcher.
        </div>
      );
    }
    const list = rowsByProject.map.get(project.id) ?? [];
    return (
      <div className="flex flex-col gap-10">
        <ProjectBand
          project={project}
          rows={list}
          renderCard={renderCard}
          renderMenu={renderProjectMenu}
          onRenameProject={onRenameProject}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10">
      {sortedProjects.map((project) => {
        const list = rowsByProject.map.get(project.id) ?? [];
        return (
          <ProjectBand
            key={project.id}
            project={project}
            rows={list}
            renderCard={renderCard}
            renderMenu={renderProjectMenu}
            onRenameProject={onRenameProject}
          />
        );
      })}

      {/* "Inbox" reframed as a soft, collapsed-by-default "Unfiled" expander.
          The user feedback was that the Inbox vibe implies a queue that
          needs processing — but cards without a project should not feel
          urgent. They live primarily under their swim lanes (Today / This
          week / Later) up top; this expander is just a way to see the full
          list and drag-assign them when desired. */}
      {sortedProjects.length === 0 && inboxRows.length > 0 ? (
        <UnfiledExpander
          rows={inboxRows}
          renderCard={renderCard}
          openByDefault
          headerHint="No projects yet — these lists are visible under your Today / This week / Later lanes above. Create a project to file them."
        />
      ) : inboxRows.length > 0 ? (
        <UnfiledExpander rows={inboxRows} renderCard={renderCard} />
      ) : null}

      {archivedHasRows ? (
        <ArchivedSection
          archivedProjects={archivedProjects}
          rowsByProject={rowsByProject.map}
          renderCard={renderCard}
        />
      ) : null}
    </div>
  );
}

interface BandProps {
  rows: ProjectsPivotRow[];
  renderCard: (row: ProjectsPivotRow) => ReactNode;
}

interface ProjectBandProps extends BandProps {
  project: TodoProject;
  renderMenu: (
    project: TodoProject,
    summary: { active: number; completed: number },
    actions: { enterRenameMode: () => void },
  ) => ReactNode;
  onRenameProject?: (projectId: string, newName: string) => Promise<void> | void;
}

/**
 * Per-column projection entry. The `row` is the canonical TaskPlan/DayFocus
 * document; `visibleItemIds` narrows what gets rendered for that column.
 *
 *  - When a card has *mixed* statuses (e.g. 2 todo + 1 in_progress), it
 *    appears in two columns: To do (with the 2 todo items) AND Doing
 *    (with the 1 in_progress item). Same Firestore document, two visual
 *    cells.
 *  - When a card has no items at all, it falls into the column reported
 *    by `workspaceKanbanColumnForTask` (currently `todo` for empty), with
 *    `visibleItemIds = undefined` so the card renders normally.
 */
export type ColumnProjection = {
  row: ProjectsPivotRow;
  visibleItemIds?: ReadonlySet<string>;
};

function actionItemStatusOf(item: { status?: ActionItemStatus; completed: boolean }): ActionItemStatus {
  if (item.status) return item.status;
  return item.completed ? 'done' : 'todo';
}

function rowsByColumn(rows: ProjectsPivotRow[]): Record<ActionItemStatus, ColumnProjection[]> {
  const out: Record<ActionItemStatus, ColumnProjection[]> = {
    todo: [],
    in_progress: [],
    done: [],
  };
  for (const r of rows) {
    if (r.task.actionItems.length === 0) {
      // No items to project — fall back to the legacy "where does this card live?" rule.
      out[workspaceKanbanColumnForTask(r.task)].push({ row: r });
      continue;
    }
    const idsByStatus: Record<ActionItemStatus, string[]> = { todo: [], in_progress: [], done: [] };
    for (const item of r.task.actionItems) {
      idsByStatus[actionItemStatusOf(item)].push(item.id);
    }
    (['todo', 'in_progress', 'done'] as ActionItemStatus[]).forEach((status) => {
      const ids = idsByStatus[status];
      if (ids.length > 0) {
        out[status].push({ row: r, visibleItemIds: new Set(ids) });
      }
    });
  }
  return out;
}

function rowSummary(rows: ProjectsPivotRow[]): { active: number; completed: number } {
  let completed = 0;
  for (const r of rows) {
    const done = r.kind === 'task'
      ? isTaskListFullyCompleted(r.task)
      : isFocusFullyCompleted(r.focus);
    if (done) completed++;
  }
  return { active: rows.length - completed, completed };
}

function ProjectBand({
  project,
  rows,
  renderCard,
  renderMenu,
  onRenameProject,
}: ProjectBandProps) {
  const tokens = project.colorKey ? TODO_PROJECT_COLOR_TOKENS[project.colorKey] : null;
  const summary = rowSummary(rows);
  const grouped = rowsByColumn(rows);
  const [renaming, setRenaming] = useState(false);

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-center gap-2.5">
        <span
          aria-hidden
          style={{
            width: 12,
            height: 12,
            borderRadius: 999,
            background: tokens?.fg ?? 'var(--color-text-muted)',
            flexShrink: 0,
          }}
        />
        <EditableProjectTitle
          project={project}
          editing={renaming}
          onEnterEdit={() => setRenaming(true)}
          onCommit={async (next) => {
            setRenaming(false);
            if (!onRenameProject) return;
            if (next === project.name) return;
            await onRenameProject(project.id, next);
          }}
          onCancel={() => setRenaming(false)}
          editable={Boolean(onRenameProject)}
        />
        <p className="text-[11px] text-[var(--color-text-muted)]">
          {summary.active} open
          {summary.completed > 0 ? ` · ${summary.completed} done` : ''}
        </p>
        <div className="ml-auto">
          {renderMenu(project, summary, { enterRenameMode: () => setRenaming(true) })}
        </div>
      </header>
      <ColumnsGrid
        grouped={grouped}
        renderCard={renderCard}
        emptyHint="Drop a list here, or assign one from the Schedule view."
        projectKey={project.id}
      />
    </section>
  );
}

/**
 * Click-to-edit project title. Renders as plain `<h2>` until the user clicks
 * (or the menu requests rename), then swaps to a focused input. Esc cancels,
 * Enter (or blur) commits via the parent's `onCommit`.
 */
function EditableProjectTitle({
  project,
  editing,
  onEnterEdit,
  onCommit,
  onCancel,
  editable,
}: {
  project: TodoProject;
  editing: boolean;
  onEnterEdit: () => void;
  onCommit: (next: string) => Promise<void> | void;
  onCancel: () => void;
  editable: boolean;
}) {
  const [draft, setDraft] = useState(project.name);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep the draft in sync when the canonical name changes (e.g. another tab).
  useEffect(() => {
    setDraft(project.name);
  }, [project.name]);

  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editing]);

  const commit = useCallback(() => {
    const trimmed = draft.replace(/\s+/g, ' ').trim();
    if (!trimmed) {
      // Empty rename = no-op + revert.
      setDraft(project.name);
      onCancel();
      return;
    }
    void onCommit(trimmed);
  }, [draft, project.name, onCommit, onCancel]);

  const handleKey = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(project.name);
      onCancel();
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        maxLength={120}
        aria-label="Rename project"
        className="text-sm font-semibold text-[var(--color-text-primary)] bg-transparent border-b border-[var(--color-border-focus)] outline-none px-0.5 py-0 min-w-[8ch]"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={editable ? onEnterEdit : undefined}
      title={editable ? 'Click to rename' : undefined}
      disabled={!editable}
      className={`group inline-flex items-center gap-1 text-left text-sm font-semibold text-[var(--color-text-primary)] rounded px-1 -mx-1 ${
        editable ? 'hover:bg-[var(--color-surface)] cursor-text' : 'cursor-default'
      }`}
    >
      <span>{project.name}</span>
      {editable ? (
        <Pencil
          size={11}
          className="opacity-0 group-hover:opacity-60 transition-opacity text-[var(--color-text-muted)]"
        />
      ) : null}
    </button>
  );
}

/**
 * Soft, collapsed-by-default expander that holds cards without a project.
 *
 * Replaces the loud "Inbox" Kanban band that used to sit at the bottom of
 * Projects view. Per user feedback the Inbox vibe ("a queue you must
 * process") was wrong — cards without a project are not unprocessed work,
 * they simply haven't been filed into an initiative yet. New tasks default
 * to a swim lane (Today / This week / Later) so they're already discoverable
 * via the Schedule chips above; this expander is a quiet "see + file"
 * affordance, not a backlog.
 */
function UnfiledExpander({
  rows,
  renderCard,
  openByDefault = false,
  headerHint,
}: BandProps & { openByDefault?: boolean; headerHint?: string }) {
  const [open, setOpen] = useState(openByDefault);
  const summary = rowSummary(rows);
  const grouped = rowsByColumn(rows);

  return (
    <section className="pt-4 border-t border-dashed border-[var(--color-border)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
        aria-expanded={open}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Inbox size={14} className="text-[var(--color-text-muted)]" />
        <span>Unfiled lists ({rows.length})</span>
        <span className="text-xs font-normal text-[var(--color-text-muted)]">
          {summary.active} open{summary.completed > 0 ? ` · ${summary.completed} done` : ''}
        </span>
      </button>
      {headerHint ? (
        <p className="text-[11px] text-[var(--color-text-muted)] mt-1.5 ml-6 max-w-[680px] leading-snug">
          {headerHint}
        </p>
      ) : null}
      {open ? (
        <div className="mt-3">
          <ColumnsGrid
            grouped={grouped}
            renderCard={renderCard}
            emptyHint="Nothing in this column."
            projectKey={PROJECT_SWITCHER_INBOX_KEY}
          />
        </div>
      ) : null}
    </section>
  );
}

/**
 * Standalone Inbox band — used only when the project switcher explicitly
 * targets Inbox so the user has asked to see these cards in full Kanban.
 */
function InboxBand({ rows, renderCard }: BandProps) {
  const summary = rowSummary(rows);
  const grouped = rowsByColumn(rows);
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-center gap-2.5">
        <Inbox size={14} className="text-[var(--color-text-muted)]" />
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Unfiled lists</h2>
        <p className="text-[11px] text-[var(--color-text-muted)]">
          {rows.length === 0 ? 'Nothing here yet' : `${summary.active} open${summary.completed > 0 ? ` · ${summary.completed} done` : ''}`}
        </p>
      </header>
      {rows.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)] py-2">
          Lists without a project will appear here. New tasks land in your Today / This week / Later lanes by default.
        </p>
      ) : (
        <ColumnsGrid
          grouped={grouped}
          renderCard={renderCard}
          emptyHint="Nothing in this column."
          projectKey={PROJECT_SWITCHER_INBOX_KEY}
        />
      )}
    </section>
  );
}

function ColumnsGrid({
  grouped,
  renderCard,
  emptyHint,
  /** When provided, each column becomes a drop target. Pass `null` to render
   *  a non-interactive grid (used by the archived section). */
  projectKey,
}: {
  grouped: Record<ActionItemStatus, ColumnProjection[]>;
  renderCard: ProjectsPivotProps['renderCard'];
  emptyHint: string;
  projectKey?: string | null;
}) {
  // Track any active workspace drag so columns visibly advertise themselves
  // as drop targets. Without this, columns only highlight on hover — users
  // grabbed a card and saw nothing change anywhere on the page, so it looked
  // like there were no drop targets at all.
  const [dragging, setDragging] = useState(false);
  useDndMonitor({
    onDragStart: () => setDragging(true),
    onDragCancel: () => setDragging(false),
    onDragEnd: () => setDragging(false),
  });

  return (
    <div className="flex flex-col gap-3 md:flex-row md:overflow-x-auto md:pb-2">
      {WS_COLUMNS.map((col) => {
        const inCol = grouped[col.id];
        return (
          <ProjectColumnCell
            key={col.id}
            label={col.label}
            status={col.id}
            rows={inCol}
            renderCard={renderCard}
            emptyHint={emptyHint}
            dragging={dragging}
            dropId={
              projectKey
                ? `${PROJECT_COLUMN_DROP_PREFIX}${projectKey}:${col.id}`
                : null
            }
          />
        );
      })}
    </div>
  );
}

function ProjectColumnCell({
  label,
  rows,
  renderCard,
  emptyHint,
  dropId,
  dragging,
}: {
  label: string;
  status: ActionItemStatus;
  rows: ColumnProjection[];
  renderCard: ProjectsPivotProps['renderCard'];
  emptyHint: string;
  dropId: string | null;
  dragging: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: dropId ?? '__noop_col__',
    disabled: dropId === null,
  });

  const isEmpty = rows.length === 0;
  const interactive = dropId !== null;

  // Visual stack: hover (isOver) wins; mid-drag standby is a softer dashed
  // outline so the user can see "yes, you can drop here".
  let cellOutline: string | undefined;
  let cellBackground: string | undefined;
  if (isOver) {
    cellOutline = `2px dashed ${brandColors.lightBlue}`;
    cellBackground = `${brandColors.lightBlue}0F`;
  } else if (dragging && interactive) {
    cellOutline = `1px dashed ${brandColors.lightBlue}66`;
    cellBackground = `${brandColors.lightBlue}06`;
  }

  return (
    <div
      ref={setNodeRef}
      className="flex-1 min-w-0 md:min-w-[280px] md:basis-0 flex flex-col gap-3 rounded-xl p-2 transition-colors"
      style={
        cellOutline
          ? {
              outline: cellOutline,
              outlineOffset: 4,
              background: cellBackground,
            }
          : undefined
      }
    >
      <div className="flex items-center gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
          {label}
        </p>
        {dragging && interactive ? (
          <span
            className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
            style={{
              color: isOver ? brandColors.lightBlue : 'var(--color-text-muted)',
              background: isOver ? `${brandColors.lightBlue}1F` : 'transparent',
              border: `1px solid ${isOver ? brandColors.lightBlue : 'var(--color-border)'}`,
            }}
          >
            {isOver ? 'Drop to set' : 'Drop target'}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-3 min-h-[60px]">
        {isEmpty ? (
          <div
            className="rounded-xl border border-dashed py-8 px-3 text-center text-xs transition-colors"
            style={{
              borderColor: isOver
                ? brandColors.lightBlue
                : dragging && interactive
                ? `${brandColors.lightBlue}66`
                : 'var(--color-border)',
              background: isOver
                ? `${brandColors.lightBlue}14`
                : dragging && interactive
                ? `${brandColors.lightBlue}08`
                : 'var(--color-surface)',
              color: isOver ? brandColors.lightBlue : 'var(--color-text-muted)',
            }}
          >
            {isOver ? `Drop here → ${label}` : emptyHint}
          </div>
        ) : (
          rows.map((projection) => {
            const { row, visibleItemIds } = projection;
            // Each projection appears at most once per column (`rows` is
            // already filtered to this column), so the row id is unique
            // here — no need to encode the column or visibility into key.
            return (
              <div key={`${row.kind}:${row.id}`}>
                {renderCard(row, { visibleItemIds })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function ArchivedSection({
  archivedProjects,
  rowsByProject,
  renderCard,
}: {
  archivedProjects: TodoProject[];
  rowsByProject: Map<string, ProjectsPivotRow[]>;
  renderCard: (row: ProjectsPivotRow) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const totalCards = useMemo(
    () => archivedProjects.reduce((sum, p) => sum + (rowsByProject.get(p.id)?.length ?? 0), 0),
    [archivedProjects, rowsByProject]
  );

  return (
    <section className="pt-4 border-t border-dashed border-[var(--color-border)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span>Archived projects ({archivedProjects.length})</span>
        <span className="text-xs font-normal text-[var(--color-text-muted)]">
          {totalCards} list{totalCards === 1 ? '' : 's'} still attached
        </span>
      </button>
      {open ? (
        <div className="mt-3 flex flex-col gap-6">
          {archivedProjects.map((project) => {
            const list = rowsByProject.get(project.id) ?? [];
            if (list.length === 0) return null;
            const grouped = rowsByColumn(list);
            const tokens = project.colorKey ? TODO_PROJECT_COLOR_TOKENS[project.colorKey] : null;
            return (
              <div key={project.id} className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: tokens?.fg ?? 'var(--color-text-muted)',
                      opacity: 0.6,
                    }}
                  />
                  <h3 className="text-sm font-medium text-[var(--color-text-secondary)]">
                    {project.name}
                  </h3>
                  <span className="text-[11px] text-[var(--color-text-muted)]">archived</span>
                </div>
                <ColumnsGrid grouped={grouped} renderCard={renderCard} emptyHint="Empty column." />
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

interface ProjectHeaderMenuProps {
  onCascade: (target: TodoSwimLane) => void;
  onArchive: () => void;
  isArchived: boolean;
  onUnarchive: () => void;
  onTogglePin: () => void;
  isPinned: boolean;
  /** Triggered by the menu's "Rename project…" item. Parent flips inline edit on. */
  onRenameRequested?: () => void;
  onDeleteRequested: () => void;
}

/**
 * Three-dot menu for a project header in the Projects pivot.
 * Surfaces cascade actions and archive/pin controls.
 */
export function ProjectHeaderMenu({
  onCascade,
  onArchive,
  isArchived,
  onUnarchive,
  onTogglePin,
  isPinned,
  onRenameRequested,
  onDeleteRequested,
}: ProjectHeaderMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded-md text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text-primary)]"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Project menu"
      >
        <MoreHorizontal size={16} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-[220px] z-30 flank-card-elevated"
          style={{
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 12,
            padding: 6,
            boxShadow: '0 10px 26px rgba(0, 0, 0, 0.18)',
          }}
        >
          {onRenameRequested ? (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onRenameRequested();
                }}
                className="w-full flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded-md text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]"
              >
                <Pencil size={13} className="text-[var(--color-text-muted)]" />
                Rename project…
              </button>
              <div className="my-1 border-t border-[var(--color-border)]" />
            </>
          ) : null}
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)] px-2 pt-1.5">
            Prioritise project
          </p>
          {(['today', 'this_week', 'later'] as TodoSwimLane[]).map((lane) => (
            <button
              key={lane}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onCascade(lane);
              }}
              className="w-full text-left text-sm px-2 py-1.5 rounded-md text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]"
            >
              Move all to {TODO_LANE_LABELS[lane]}
            </button>
          ))}
          <div className="my-1 border-t border-[var(--color-border)]" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onTogglePin();
            }}
            className="w-full text-left text-sm px-2 py-1.5 rounded-md text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]"
          >
            {isPinned ? 'Unpin project' : 'Pin to top'}
          </button>
          {isArchived ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onUnarchive();
              }}
              className="w-full text-left text-sm px-2 py-1.5 rounded-md text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]"
            >
              Unarchive
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onArchive();
              }}
              className="w-full text-left text-sm px-2 py-1.5 rounded-md text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]"
            >
              Archive project
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDeleteRequested();
            }}
            className="w-full text-left text-sm px-2 py-1.5 rounded-md text-[var(--color-error)] hover:bg-[var(--color-surface)]"
          >
            Delete project…
          </button>
        </div>
      ) : null}
    </div>
  );
}
